import React, { useState, useEffect } from 'react';
import { adminService } from '../../services/adminService';
import LoadingSpinner from '../ui/LoadingSpinner';
import { FooterData, footerService } from '../../services/footerService';

const FooterManager: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [footerData, setFooterData] = useState<FooterData>({
    companyInfo: {
      name: '',
      description: '',
      address: '',
      phone: '',
      email: '',
    },
    socialLinks: {
      facebook: '',
      instagram: '',
      linkedin: '',
      twitter: '',
    },
    copyright: '',
    quickLinks: [],
  });

  useEffect(() => {
    checkRoleAndLoadData();
  }, []);

  const checkRoleAndLoadData = async () => {
    try {
      setIsLoading(true);
      const userRole = await adminService.checkCurrentUserRole();

      if (userRole !== 'admin') {
        setError('You do not have permission to manage footer content. Please contact your administrator.');
        setIsLoading(false);
        return;
      }

      await loadFooterData();
    } catch (err) {
      console.error('Error checking role:', err);
      setError('Error checking permissions');
      setIsLoading(false);
    }
  };

  const loadFooterData = async () => {
    try {
      const data = await footerService.getFooterData();
      setFooterData(data);
      setError(null);
    } catch (err) {
      setError('Error loading footer data');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (
    section: keyof FooterData,
    field: string,
    value: string
  ) => {
    setFooterData(prev => ({
      ...prev,
      [section]: typeof prev[section] === 'object'
        ? {
            ...(prev[section] as object),
            [field]: value
          }
        : value
    }));
  };

  const handleQuickLinkChange = (
    id: string,
    field: 'label' | 'url',
    value: string
  ) => {
    setFooterData(prev => ({
      ...prev,
      quickLinks: prev.quickLinks.map(link =>
        link.id === id ? { ...link, [field]: value } : link
      )
    }));
  };

  const addQuickLink = () => {
    setFooterData(prev => ({
      ...prev,
      quickLinks: [
        ...prev.quickLinks,
        { id: crypto.randomUUID(), label: '', url: '' }
      ]
    }));
  };

  const removeQuickLink = (id: string) => {
    setFooterData(prev => ({
      ...prev,
      quickLinks: prev.quickLinks.filter(link => link.id !== id)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      await footerService.updateFooterData(footerData);
      setError(null);
    } catch (err) {
      setError('Error saving footer data');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Footer Management</h2>
          <p className="mt-1 sm:mt-2 text-sm text-gray-600">
            Manage your website's footer content and links.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 sm:mb-6 bg-red-50 border-l-4 border-red-400 p-3 sm:p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Company Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Company Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="sm:col-span-2">
                <label htmlFor="company-name" className="block text-sm font-medium text-gray-900 mb-1">
                  Company Name
                </label>
                <input
                  type="text"
                  id="company-name"
                  value={footerData.companyInfo.name}
                  onChange={(e) => handleInputChange('companyInfo', 'name', e.target.value)}
                  className="block w-full px-4 py-2.5 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>
              
              <div className="sm:col-span-2">
                <label htmlFor="description" className="block text-sm font-medium text-gray-900 mb-1">
                  Description
                </label>
                <textarea
                  id="description"
                  rows={3}
                  value={footerData.companyInfo.description}
                  onChange={(e) => handleInputChange('companyInfo', 'description', e.target.value)}
                  className="block w-full px-4 py-2.5 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="address" className="block text-sm font-medium text-gray-900 mb-1">
                  Address
                </label>
                <textarea
                  id="address"
                  rows={2}
                  value={footerData.companyInfo.address}
                  onChange={(e) => handleInputChange('companyInfo', 'address', e.target.value)}
                  className="block w-full px-4 py-2.5 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-900 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  id="phone"
                  value={footerData.companyInfo.phone}
                  onChange={(e) => handleInputChange('companyInfo', 'phone', e.target.value)}
                  className="block w-full px-4 py-2.5 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={footerData.companyInfo.email}
                  onChange={(e) => handleInputChange('companyInfo', 'email', e.target.value)}
                  className="block w-full px-4 py-2.5 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Social Links */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Social Media Links</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label htmlFor="facebook" className="block text-sm font-medium text-gray-900 mb-1">
                  Facebook URL
                </label>
                <input
                  type="url"
                  id="facebook"
                  value={footerData.socialLinks.facebook}
                  onChange={(e) => handleInputChange('socialLinks', 'facebook', e.target.value)}
                  className="block w-full px-4 py-2.5 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="https://facebook.com/..."
                />
              </div>

              <div>
                <label htmlFor="instagram" className="block text-sm font-medium text-gray-900 mb-1">
                  Instagram URL
                </label>
                <input
                  type="url"
                  id="instagram"
                  value={footerData.socialLinks.instagram}
                  onChange={(e) => handleInputChange('socialLinks', 'instagram', e.target.value)}
                  className="block w-full px-4 py-2.5 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="https://instagram.com/..."
                />
              </div>

              <div>
                <label htmlFor="linkedin" className="block text-sm font-medium text-gray-900 mb-1">
                  LinkedIn URL
                </label>
                <input
                  type="url"
                  id="linkedin"
                  value={footerData.socialLinks.linkedin}
                  onChange={(e) => handleInputChange('socialLinks', 'linkedin', e.target.value)}
                  className="block w-full px-4 py-2.5 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="https://linkedin.com/..."
                />
              </div>

              <div>
                <label htmlFor="twitter" className="block text-sm font-medium text-gray-900 mb-1">
                  Twitter URL
                </label>
                <input
                  type="url"
                  id="twitter"
                  value={footerData.socialLinks.twitter}
                  onChange={(e) => handleInputChange('socialLinks', 'twitter', e.target.value)}
                  className="block w-full px-4 py-2.5 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="https://twitter.com/..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Quick Links</h3>
              <button
                type="button"
                onClick={addQuickLink}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Link
              </button>
            </div>
            
            <div className="space-y-4">
              {footerData.quickLinks.map((link, index) => (
                <div key={link.id} className="flex items-start space-x-4">
                  <div className="flex-grow grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor={`link-label-${link.id}`} className="block text-sm font-medium text-gray-900 mb-1">
                        Label
                      </label>
                      <input
                        type="text"
                        id={`link-label-${link.id}`}
                        value={link.label}
                        onChange={(e) => handleQuickLinkChange(link.id, 'label', e.target.value)}
                        className="block w-full px-4 py-2.5 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        placeholder="Link Label"
                      />
                    </div>
                    <div>
                      <label htmlFor={`link-url-${link.id}`} className="block text-sm font-medium text-gray-900 mb-1">
                        URL
                      </label>
                      <input
                        type="url"
                        id={`link-url-${link.id}`}
                        value={link.url}
                        onChange={(e) => handleQuickLinkChange(link.id, 'url', e.target.value)}
                        className="block w-full px-4 py-2.5 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeQuickLink(link.id)}
                    className="mt-6 p-2 -m-2 text-gray-400 hover:text-red-600 transition-colors duration-200"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Copyright Notice</h3>
            <div>
              <label htmlFor="copyright" className="block text-sm font-medium text-gray-900 mb-1">
                Copyright Text
              </label>
              <input
                type="text"
                id="copyright"
                value={footerData.copyright}
                onChange={(e) => handleInputChange('copyright', '', e.target.value)}
                className="block w-full px-4 py-2.5 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder="© 2024 Your Company. All rights reserved."
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <LoadingSpinner size="sm" className="mr-3" />
                Saving Changes...
              </>
            ) : (
              <>
                <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Save Changes
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FooterManager; 